import React, { useState, useEffect } from 'react';
import { Container, Box, Tabs, Tab, Typography, Button, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, useMediaQuery, useTheme, IconButton, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import ErrorIcon from '@mui/icons-material/Error';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SettingsIcon from '@mui/icons-material/Settings';
import FlagIcon from '@mui/icons-material/Flag';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BuildIcon from '@mui/icons-material/Build';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MenuIcon from '@mui/icons-material/Menu';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import KeyIcon from '@mui/icons-material/Key';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CodeIcon from '@mui/icons-material/Code';
import ApiIcon from '@mui/icons-material/Api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFirebase } from '../../context/FirebaseContext';
import { getVersionInfoV2NoAuth } from '../../api/system';
import packageJson from '../../../package.json';

// Tab Components
import UsersTab from './UsersTab';
import UsersHistoryTab from './UsersHistoryTab';
import ErrorsTab from './ErrorsTab';
import KeyValueTab from './KeyValueTab';
import FeatureFlagsTab from './FeatureFlagsTab';
import CopilotModelsTab from './CopilotModelsTab';
import ConceptGateSettingsTab from './ConceptGateSettingsTab';
import ServicesTab from './ServicesTab';
import EconomicCalendarTab from './EconomicCalendarTab';
import AlphaAccessTab from './AlphaAccessTab';
import FeedbackTab from './FeedbackTab';
import SubscriptionSettingsTab from './SubscriptionSettingsTab';
import OAuthClientsTab from './OAuthClientsTab';
import ServiceApiKeysTab from './ServiceApiKeysTab';

import { Logo } from '../layout';
import './AdminView.css';

import { analytics } from '../../firebaseConfig';
import { logEvent } from 'firebase/analytics';

const TAB_NAMES = [
  'users', 'users_history', 'errors', 'key_value', 'feature_flags',
  'models', 'concept_gate', 'services', 'calendar', 'alpha_access',
  'feedback', 'subscriptions', 'oauth_clients', 'service_api_keys'
];

// Tab configuration with categories (ordered by index)
const TAB_CONFIG = [
  // User Management
  { index: 0, label: 'Users', icon: <PeopleIcon />, category: 'User Management' },
  { index: 1, label: 'Users History', icon: <HistoryIcon />, category: 'User Management' },
  { index: 2, label: 'Errors', icon: <ErrorIcon />, category: 'User Management' },
  // System Configuration
  { index: 3, label: 'Key-Value', icon: <SettingsIcon />, category: 'System Configuration' },
  { index: 4, label: 'Feature Flags', icon: <FlagIcon />, category: 'System Configuration' },
  { index: 5, label: 'Models', icon: <PsychologyIcon />, category: 'System Configuration' },
  { index: 6, label: 'ConceptGate', icon: <BuildIcon />, category: 'System Configuration' },
  // Services & Content
  { index: 7, label: 'Services', icon: <BuildIcon />, category: 'Services & Content' },
  { index: 8, label: 'Calendar', icon: <CalendarTodayIcon />, category: 'Services & Content' },
  { index: 9, label: 'Alpha Access', icon: <StarIcon />, category: 'Services & Content' },
  { index: 10, label: 'Feedback', icon: <FeedbackIcon />, category: 'User Management' },
  { index: 11, label: 'Subscriptions', icon: <CreditCardIcon />, category: 'Services & Content' },
  { index: 12, label: 'OAuth Clients', icon: <VpnKeyIcon />, category: 'System Configuration' },
  { index: 13, label: 'Service API Keys', icon: <KeyIcon />, category: 'System Configuration' },
];

const DRAWER_WIDTH = 280;

const AdminView = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const { auth } = useFirebase();
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam ? parseInt(tabParam, 10) : 0;
  });
  const [errorFilterUserId, setErrorFilterUserId] = useState('');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [backendVersion, setBackendVersion] = useState('Loading...');
  const [copiedVersion, setCopiedVersion] = useState(null);

  // Sync tab with URL when URL changes externally
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (tabIndex !== activeTab && tabIndex >= 0 && tabIndex <= 13) {
        setActiveTab(tabIndex);
      }
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    const fetchVersion = async () => {
      await getVersionInfoV2NoAuth((data) => {
        if (data && !data.error) {
          setBackendVersion(data);
        } else {
          setBackendVersion('Unavailable');
        }
      });
    };

    fetchVersion();
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    logEvent(analytics, 'admin_tab_switched', { tab_name: TAB_NAMES[newValue] || 'unknown' });
    // Update URL without full navigation
    setSearchParams({ tab: newValue });
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleSidebarTabClick = (tabIndex) => {
    handleTabChange(null, tabIndex);
  };

  const handleBackClick = () => {
    navigate('/');
  };

  const handleCopyVersion = (type, value) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedVersion(type);
      setTimeout(() => setCopiedVersion(null), 2000);
    });
  };

  const handleViewUserLogs = (userId) => {
    setErrorFilterUserId(userId);
    setActiveTab(2); // Switch to Errors tab (now index 2)
  };

  // Group tabs by category
  const groupedTabs = TAB_CONFIG.reduce((acc, tab) => {
    if (!acc[tab.category]) {
      acc[tab.category] = [];
    }
    acc[tab.category].push(tab);
    return acc;
  }, {});

  // Render sidebar navigation
  const renderSidebar = () => (
    <List sx={{ width: '100%', pt: 2 }}>
      {Object.entries(groupedTabs).map(([category, tabs]) => (
        <React.Fragment key={category}>
          <ListItem sx={{ px: 2, py: 1 }}>
            <ListItemText
              primary={category}
              primaryTypographyProps={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'text.secondary'
              }}
            />
          </ListItem>
          {tabs.map((tab) => (
            <ListItem key={tab.index} disablePadding>
              <ListItemButton
                selected={activeTab === tab.index}
                onClick={() => handleSidebarTabClick(tab.index)}
                sx={{
                  pl: 3,
                  py: 1.5,
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                    pl: 'calc(12px + 3px)'
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: activeTab === tab.index ? 'primary.main' : 'inherit' }}>
                  {tab.icon}
                </ListItemIcon>
                <ListItemText
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: activeTab === tab.index ? 600 : 400
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          <Divider sx={{ my: 1 }} />
        </React.Fragment>
      ))}
    </List>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              height: '100%'
            },
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Admin Dashboard
            </Typography>
          </Box>
          {renderSidebar()}
        </Drawer>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              backgroundColor: 'var(--bg-secondary)',
            },
          }}
        >
          <Box sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: 'calc(env(safe-area-inset-top, 0px) + 16px)',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              Admin
            </Typography>
            <IconButton
              onClick={() => setMobileDrawerOpen(false)}
              size="small"
              aria-label="Close menu"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {renderSidebar()}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          backgroundColor: 'background.default',
          pb: isMobile ? 'env(safe-area-inset-bottom, 0px)' : 0,
        }}
      >
        <Container maxWidth="lg" className="ntb-admin-container">
          {/* Mobile Header */}
          {isMobile && (
            <Box className="admin-mobile-header">
              <IconButton
                onClick={() => setMobileDrawerOpen(true)}
                size="small"
                aria-label="Open menu"
                className="admin-mobile-menu-btn"
              >
                <MenuIcon />
              </IconButton>
              <Typography className="admin-mobile-header-title" noWrap>
                {TAB_CONFIG.find(t => t.index === activeTab)?.label || 'Admin'}
              </Typography>
              <IconButton
                onClick={handleBackClick}
                size="small"
                aria-label="Back to app"
                className="admin-mobile-back-btn"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {/* Desktop Header */}
          {!isMobile && (
            <Box className="admin-header">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackClick}
                size="small"
                className="admin-back-btn"
              >
                Back
              </Button>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/')}
              >
                <Logo size="28px" />
              </Box>
              <Box className="admin-breadcrumb">
                <span className="admin-breadcrumb-link" onClick={() => navigate('/')}>
                  NanoTradingCopilot
                </span>
                <span>/</span>
                <span className="admin-breadcrumb-current">Admin</span>
              </Box>
            </Box>
          )}

          {/* Title - Desktop only */}
          {!isMobile && (
            <Typography variant="h5" className="admin-title">
              {TAB_CONFIG.find(t => t.index === activeTab)?.label || 'Admin Dashboard'}
            </Typography>
          )}

          {/* Version Cards */}
          <Box className="admin-version-cards">
            <Box className="admin-version-card">
              <Box className="admin-version-card-icon admin-version-card-icon-frontend">
                <CodeIcon fontSize="small" />
              </Box>
              <Box className="admin-version-card-info">
                <span className="admin-version-card-label">Frontend</span>
                <span className="admin-version-card-value">{packageJson.version}</span>
              </Box>
              <Tooltip title={copiedVersion === 'frontend' ? 'Copied' : 'Copy version'}>
                <IconButton
                  size="small"
                  onClick={() => handleCopyVersion('frontend', packageJson.version)}
                  className="admin-version-copy-btn"
                >
                  {copiedVersion === 'frontend' ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
            <Box className="admin-version-card">
              <Box className="admin-version-card-icon admin-version-card-icon-backend">
                <ApiIcon fontSize="small" />
              </Box>
              <Box className="admin-version-card-info">
                <span className="admin-version-card-label">Backend</span>
                <span className="admin-version-card-value">{backendVersion}</span>
              </Box>
              <Tooltip title={copiedVersion === 'backend' ? 'Copied' : 'Copy version'}>
                <IconButton
                  size="small"
                  onClick={() => handleCopyVersion('backend', String(backendVersion))}
                  className="admin-version-copy-btn"
                >
                  {copiedVersion === 'backend' ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Tabs - Keep for horizontal navigation on desktop */}
          {!isMobile && (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              className="admin-tabs"
            >
              {TAB_CONFIG.sort((a, b) => a.index - b.index).map((tab) => (
                <Tab key={tab.index} label={tab.label} />
              ))}
            </Tabs>
          )}

          {/* Tab Content */}
          <Box className="admin-tab-content">
            {activeTab === 0 && (
              <UsersTab onViewLogs={handleViewUserLogs} />
            )}

            {activeTab === 1 && (
              <UsersHistoryTab />
            )}

            {activeTab === 2 && (
              <ErrorsTab initialUserId={errorFilterUserId} />
            )}

            {activeTab === 3 && (
              <KeyValueTab auth={auth} />
            )}

            {activeTab === 4 && (
              <FeatureFlagsTab auth={auth} />
            )}

            {activeTab === 5 && (
              <CopilotModelsTab />
            )}

            {activeTab === 6 && (
              <ConceptGateSettingsTab auth={auth} />
            )}

            {activeTab === 7 && (
              <ServicesTab />
            )}

            {activeTab === 8 && (
              <EconomicCalendarTab />
            )}

            {activeTab === 9 && (
              <AlphaAccessTab />
            )}

            {activeTab === 10 && (
              <FeedbackTab />
            )}

            {activeTab === 11 && (
              <SubscriptionSettingsTab auth={auth} />
            )}

            {activeTab === 12 && (
              <OAuthClientsTab />
            )}

            {activeTab === 13 && (
              <ServiceApiKeysTab />
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AdminView;
